import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, MapPin, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { api } from '@/lib/api';
import { prettyModelName } from '@/lib/utils';
import type { Annotation, ModelFile } from '@/lib/types';
import { ModelThumbnail } from './ModelThumbnail';
import { AnnotationEditorDialog } from './AnnotationEditorDialog';

/** Basename lookup for the /models/:name/annotations endpoint — item.model is a full versioned URL ("/assets/models/Duck.glb?v=123..."). */
function modelBasename(modelPath: string) {
  return modelPath.split('/').pop()?.split('?')[0] ?? modelPath;
}

/**
 * One virtual screen's live web content on a model — e.g. one monitor on
 * a SOC-desk model. position/rotation are the same "x y z" vec3 strings
 * used by the model's own position/rotation, in the model's local space;
 * width/height are in the same local units. `id` is a client-only key for
 * React list rendering, never persisted.
 */
export interface OverlayDraft {
  id: string;
  url: string;
  position: string;
  rotation: string;
  width: number;
  height: number;
}

export function newOverlayDraft(): OverlayDraft {
  return {
    id: Math.random().toString(36).slice(2),
    url: '',
    position: '0 0 0',
    rotation: '0 0 0',
    width: 1,
    height: 0.6,
  };
}

interface DraftItem {
  model: string;
  externalURL: string;
  /**
   * A-Frame world-space scale applied uniformly to the entity on the
   * AR marker. Different GLBs export at wildly different sizes; the
   * creator tunes this value until the model looks right on the marker.
   * Persisted as an A-Frame vec3 string "s s s".
   */
  scale: number;
  /**
   * Live web content composited onto this model as CSS3D iframes on the
   * AR client — one entry per virtual screen. Entries with an empty url
   * are dropped on save.
   */
  overlays: OverlayDraft[];
  /**
   * Per-sequence annotation override — see Asset.annotations. `null`/
   * `undefined` means this asset inherits the model's own default
   * annotations (edited separately, per-model, not per-sequence); a
   * populated array (even an empty one) overrides them just here.
   */
  annotations?: Annotation[] | null;
}

interface Props {
  models: ModelFile[];
  name: string;
  onNameChange: (n: string) => void;
  nameReadOnly?: boolean;
  items: DraftItem[];
  onItemsChange: (items: DraftItem[]) => void;
}

export function SequenceEditor({
  models,
  name,
  onNameChange,
  nameReadOnly,
  items,
  onItemsChange,
}: Props) {
  const [libraryFilter, setLibraryFilter] = useState('');
  // Captured via ref callback so ModelThumbnail's IntersectionObserver
  // can use the real scroll container as its root. Without this every
  // tile inside an `overflow-y: auto` ancestor is treated as "in view"
  // by the default (viewport) root and mounts a live <model-viewer>,
  // instantly OOM'ing the tab in a portalled Dialog.
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const filteredLibrary = useMemo(() => {
    const q = libraryFilter.trim().toLowerCase();
    return models.filter((m) => !q || m.name.toLowerCase().includes(q));
  }, [models, libraryFilter]);

  const libraryCountLabel = libraryFilter.trim()
    ? `${filteredLibrary.length} of ${models.length} GLB${models.length === 1 ? '' : 's'}`
    : `${models.length} GLB${models.length === 1 ? '' : 's'} available`;

  const inUse = new Set(items.map((i) => i.model));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.model === active.id);
    const newIndex = items.findIndex((i) => i.model === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onItemsChange(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_360px]">
      {/* --- Sequence being built ------------------------------- */}
      <div className="flex min-h-[420px] flex-col gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="seq-name">Sequence name</Label>
          <Input
            id="seq-name"
            autoFocus={!nameReadOnly}
            placeholder="e.g. products, homescan-tour"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            readOnly={nameReadOnly}
            disabled={nameReadOnly}
          />
        </div>

        <div className="rounded-md border">
          <div className="flex items-center justify-between border-b px-3 py-2 text-xs text-muted-foreground">
            <span>Sequence order — drag to reorder</span>
            <span>{items.length} items</span>
          </div>
          {items.length === 0 ? (
            <div className="grid place-items-center px-3 py-8 text-center text-sm text-muted-foreground">
              Click a model on the right to add it to the sequence.
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={items.map((i) => i.model)} strategy={verticalListSortingStrategy}>
                <ol className="divide-y divide-border">
                  {items.map((item, index) => (
                    <SortableRow
                      key={item.model}
                      item={item}
                      index={index}
                      onRemove={() => onItemsChange(items.filter((i) => i.model !== item.model))}
                      onURLChange={(u) =>
                        onItemsChange(
                          items.map((i) => (i.model === item.model ? { ...i, externalURL: u } : i)),
                        )
                      }
                      onScaleChange={(s) =>
                        onItemsChange(
                          items.map((i) => (i.model === item.model ? { ...i, scale: s } : i)),
                        )
                      }
                      onOverlaysChange={(overlays) =>
                        onItemsChange(
                          items.map((i) => (i.model === item.model ? { ...i, overlays } : i)),
                        )
                      }
                      onAnnotationsChange={(annotations) =>
                        onItemsChange(
                          items.map((i) => (i.model === item.model ? { ...i, annotations } : i)),
                        )
                      }
                    />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* --- Model library ------------------------------------- */}
      {/* Structure mirrors the Sequences-tab sidebar library: the scroll
          container is a plain <div> that WRAPS the CSS grid rather than
          being the grid itself. Combining `display: grid` + `overflow: auto`
          on the same element breaks IntersectionObserver's `root` inside
          a Radix Dialog portal — tiles never report as intersecting and
          the <model-viewer>s never mount. Keep them separate. */}
      <div className="flex min-h-[420px] max-h-[70vh] flex-col gap-2 overflow-hidden rounded-md border">
        <div className="flex items-baseline justify-between px-3 pt-3">
          <Label>Model library</Label>
          <span className="text-xs text-muted-foreground">{libraryCountLabel}</span>
        </div>
        <div className="px-3">
          <Input
            placeholder="Filter…"
            value={libraryFilter}
            onChange={(e) => setLibraryFilter(e.target.value)}
          />
        </div>
        <div
          ref={setScrollRoot}
          className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        >
          <div className="grid grid-cols-2 gap-2">
            {filteredLibrary.map((m) => {
              const used = inUse.has(m.path);
              return (
                <button
                  key={m.path}
                  type="button"
                  disabled={used}
                  onClick={() =>
                    onItemsChange([
                      ...items,
                      { model: m.path, externalURL: '', scale: 3, overlays: [] },
                    ])
                  }
                  className={`group relative flex flex-col overflow-hidden rounded-md border text-left transition-colors ${
                    used
                      ? 'cursor-not-allowed opacity-40'
                      : 'hover:border-primary hover:shadow-sm'
                  }`}
                >
                  <ModelThumbnail
                    modelPath={m.path}
                    name={m.name}
                    size="sm"
                    scrollRoot={scrollRoot}
                  />
                  <div className="border-t bg-muted/30 px-2 py-1.5 text-[11px]">
                    <div className="truncate font-medium">{prettyModelName(m.path)}</div>
                    <div className="text-muted-foreground">{used ? 'Added' : 'Click to add'}</div>
                  </div>
                  {!used && (
                    <div className="pointer-events-none absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground opacity-0 transition-opacity group-hover:opacity-100">
                      <Plus className="h-3 w-3" />
                    </div>
                  )}
                </button>
              );
            })}
            {filteredLibrary.length === 0 && (
              <div className="col-span-2 grid place-items-center py-6 text-xs text-muted-foreground">
                No models match.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableRow({
  item,
  index,
  onRemove,
  onURLChange,
  onScaleChange,
  onOverlaysChange,
  onAnnotationsChange,
}: {
  item: DraftItem;
  index: number;
  onRemove: () => void;
  onURLChange: (u: string) => void;
  onScaleChange: (s: number) => void;
  onOverlaysChange: (overlays: OverlayDraft[]) => void;
  onAnnotationsChange: (annotations: Annotation[] | null) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.model,
  });
  const [overlaysOpen, setOverlaysOpen] = useState(item.overlays.length > 0);

  // The model's own default annotations — fetched once per row, used
  // both for the "inheriting N defaults" count and as the starting
  // point when switching this row into a sequence-specific override.
  // Independent of item.annotations, which is this row's OWN override
  // state (or null/undefined to inherit these).
  const [modelDefaults, setModelDefaults] = useState<Annotation[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.getModelAnnotations(modelBasename(item.model))
      .then((a) => { if (!cancelled) setModelDefaults(a); })
      .catch(() => { if (!cancelled) setModelDefaults([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.model]);
  const [annotationEditor, setAnnotationEditor] = useState<'model' | 'sequence' | null>(null);
  const isOverridden = Array.isArray(item.annotations);
  // Own text buffer for the scale field, decoupled from item.scale — a
  // plain `value={item.scale}` controlled input re-derives its displayed
  // text from the clamped numeric value on every keystroke, which fights
  // typing: a leading "0" snapped straight to clampScale's 0.001 floor
  // before the "." could be typed, and "1.50" collapsed back to "1.5" the
  // instant the trailing zero landed, since the number was already 1.5.
  // This buffer holds exactly what's on screen; onScaleChange only fires
  // once the text is a complete, valid number, and the buffer is only
  // ever reformatted on blur.
  const [scaleText, setScaleText] = useState(() => String(item.scale));

  const patchOverlay = (id: string, patch: Partial<OverlayDraft>) =>
    onOverlaysChange(item.overlays.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  const removeOverlay = (id: string) =>
    onOverlaysChange(item.overlays.filter((o) => o.id !== id));

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const clampScale = (n: number) => Math.min(100, Math.max(0.001, n));

  return (
    <li ref={setNodeRef} style={style} className="flex items-start gap-2 px-2 py-2">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-2 cursor-grab rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="mt-2 w-6 shrink-0 text-right tabular-nums text-xs text-muted-foreground">
        {index + 1}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{prettyModelName(item.model)}</div>
        <div className="mt-1 grid gap-1 sm:grid-cols-[minmax(0,1fr)_140px]">
          <Input
            placeholder="External URL (optional)"
            value={item.externalURL}
            onChange={(e) => onURLChange(e.target.value)}
            className="h-7 text-xs"
          />
          <label
            className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
            title="A-Frame world scale applied to the model on the AR marker. Larger values = bigger on-marker render."
          >
            <span className="shrink-0">Scale</span>
            <Input
              type="text"
              inputMode="decimal"
              value={scaleText}
              onChange={(e) => {
                const next = e.target.value;
                // Only plain positive decimals — no sign, no exponent,
                // nothing scale doesn't accept — but let a partial value
                // like "1." or "" sit on screen while typing, rather than
                // rejecting or reformatting it.
                if (!/^\d*\.?\d*$/.test(next)) return;
                setScaleText(next);
                const n = parseFloat(next);
                if (Number.isFinite(n) && n > 0) onScaleChange(clampScale(n));
              }}
              onBlur={() => {
                const n = parseFloat(scaleText);
                const clamped = Number.isFinite(n) && n > 0 ? clampScale(n) : 1;
                setScaleText(String(clamped));
                onScaleChange(clamped);
              }}
              className="h-7 w-full text-xs tabular-nums"
            />
          </label>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOverlaysOpen((v) => !v)}
          className="mt-1.5 h-6 self-start px-2 text-[11px]"
        >
          {item.overlays.length > 0
            ? `${item.overlays.length} screen overlay${item.overlays.length === 1 ? '' : 's'}`
            : 'Add screen overlay…'}{' '}
          {overlaysOpen ? '▲' : '▼'}
        </Button>
        {overlaysOpen && (
          <div className="mt-1.5 flex flex-col gap-2">
            {item.overlays.map((overlay, oi) => (
              <div key={overlay.id} className="grid gap-1 rounded-md border border-dashed p-2">
                <div className="flex items-center gap-1">
                  <span className="w-4 shrink-0 text-right text-[10px] text-muted-foreground">
                    {oi + 1}
                  </span>
                  <Input
                    placeholder="Live web content URL (e.g. a dashboard embed link)"
                    value={overlay.url}
                    onChange={(e) => patchOverlay(overlay.id, { url: e.target.value })}
                    className="h-7 flex-1 text-xs"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove overlay"
                    onClick={() => removeOverlay(overlay.id)}
                    className="h-7 w-7"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
                <div className="grid grid-cols-4 gap-1 pl-5">
                  <label className="text-[11px] text-muted-foreground" title="Position on the model, local vec3 'x y z'">
                    Position
                    <Input
                      value={overlay.position}
                      onChange={(e) => patchOverlay(overlay.id, { position: e.target.value })}
                      className="mt-0.5 h-7 text-xs"
                    />
                  </label>
                  <label className="text-[11px] text-muted-foreground" title="Rotation on the model, degrees vec3 'x y z'">
                    Rotation
                    <Input
                      value={overlay.rotation}
                      onChange={(e) => patchOverlay(overlay.id, { rotation: e.target.value })}
                      className="mt-0.5 h-7 text-xs"
                    />
                  </label>
                  <label className="text-[11px] text-muted-foreground" title="Width, model-local units">
                    Width
                    <Input
                      type="number"
                      step={0.05}
                      min={0.01}
                      value={overlay.width}
                      onChange={(e) => {
                        const n = parseFloat(e.target.value);
                        if (Number.isFinite(n) && n > 0) patchOverlay(overlay.id, { width: n });
                      }}
                      className="mt-0.5 h-7 text-xs tabular-nums"
                    />
                  </label>
                  <label className="text-[11px] text-muted-foreground" title="Height, model-local units">
                    Height
                    <Input
                      type="number"
                      step={0.05}
                      min={0.01}
                      value={overlay.height}
                      onChange={(e) => {
                        const n = parseFloat(e.target.value);
                        if (Number.isFinite(n) && n > 0) patchOverlay(overlay.id, { height: n });
                      }}
                      className="mt-0.5 h-7 text-xs tabular-nums"
                    />
                  </label>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 self-start text-xs"
              onClick={() => {
                onOverlaysChange([...item.overlays, newOverlayDraft()]);
                setOverlaysOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5" /> Add screen
            </Button>
          </div>
        )}

        <div className="mt-1.5 flex items-center gap-2 text-[11px]">
          <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">
            {isOverridden
              ? `Custom for this sequence (${item.annotations!.length})`
              : `Using model defaults${modelDefaults ? ` (${modelDefaults.length})` : '…'}`}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAnnotationEditor(isOverridden ? 'sequence' : 'model')}
            className="h-6 px-2 text-[11px]"
          >
            Edit…
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onAnnotationsChange(isOverridden ? null : [...(modelDefaults ?? [])])}
            className="h-6 px-2 text-[11px]"
            title={
              isOverridden
                ? "Discard this sequence's own annotations and go back to the model's defaults"
                : "Start from the model's defaults, editable just for this sequence"
            }
          >
            {isOverridden ? 'Use model defaults instead' : 'Override for this sequence'}
          </Button>
        </div>
      </div>
      <Button variant="ghost" size="icon" aria-label="Remove" onClick={onRemove} className="mt-1">
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>

      <AnnotationEditorDialog
        open={annotationEditor === 'model'}
        onOpenChange={(open) => setAnnotationEditor(open ? 'model' : null)}
        modelPath={item.model}
        scopeLabel="Default annotations for this model — used by every sequence that doesn't override them."
        annotations={modelDefaults ?? []}
        onSave={async (next) => {
          try {
            const saved = await api.saveModelAnnotations(modelBasename(item.model), next);
            setModelDefaults(saved);
            toast.success('Saved model annotations');
          } catch (err) {
            toast.error('Failed to save annotations', {
              description: err instanceof Error ? err.message : String(err),
            });
          }
        }}
      />
      <AnnotationEditorDialog
        open={annotationEditor === 'sequence'}
        onOpenChange={(open) => setAnnotationEditor(open ? 'sequence' : null)}
        modelPath={item.model}
        scopeLabel="Annotations for this sequence only — won't affect this model anywhere else."
        annotations={item.annotations ?? []}
        onSave={(next) => onAnnotationsChange(next)}
      />
    </li>
  );
}
